import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MessageSquareText,
  Video,
  Mail,
  ShieldCheck,
  CheckCircle2,
  ArrowLeft,
  AlertTriangle,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const METHODS = [
  { key: "call", icon: Phone, title: "Call a Chaplain", label: "Talk privately with an available CALLACHAP chaplain." },
  { key: "text", icon: MessageSquareText, title: "Text a Chaplain", label: "Reach out by text when writing feels easier than making a call." },
  { key: "video", icon: Video, title: "Remote Support", label: "Connect through available face-to-face or remote-support options." },
  { key: "message", icon: Mail, title: "Send a Confidential Message", label: "Send a private support message when you would prefer to write." },
];

function DemoBadge() {
  return (
    <span
      data-testid="balm-demo-badge"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-800"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
      Demonstration
    </span>
  );
}

function Confidentiality({ testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-2.5 p-3 rounded-lg bg-[#F0F4F8] border border-[#4A6FA5]/15 text-sm text-[#3B597B]"
    >
      <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} aria-hidden="true" />
      <p className="leading-relaxed">
        Your employer will not receive your name, message, or conversation details.
      </p>
    </div>
  );
}

function DemoComplete({ heading, body, onReset }) {
  return (
    <div
      data-testid="balm-demo-complete"
      className="rounded-2xl p-6 md:p-8 bg-white border border-emerald-200 shadow-sm text-center"
    >
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 mb-4">
        <CheckCircle2 className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
      </span>
      <h3 className="font-heading text-2xl tracking-tight text-slate-900">{heading}</h3>
      <p className="mt-3 text-sm md:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
        {body}
      </p>
      <p className="mt-4 text-xs text-slate-500">
        No information was transmitted or stored. This is a product demonstration.
      </p>
      <button
        type="button"
        onClick={onReset}
        data-testid="balm-demo-restart"
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold transition-colors"
      >
        Restart Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
      </button>
    </div>
  );
}

/* ----------------------------- FLOWS ----------------------------- */

function CallFlow({ onDone }) {
  const [step, setStep] = useState("choose");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  if (step === "done") {
    return (
      <DemoComplete
        heading="DEMO COMPLETE"
        body="In the live CALLACHAP service, this request would be routed securely to the appropriate chaplain."
        onReset={onDone}
      />
    );
  }

  return (
    <div className="rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm">
      <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">
        Call a chaplain
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        Choose how you'd like to connect. This is a demonstration — no phone call is placed.
      </p>

      {step === "choose" && (
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setStep("nextAvailable")}
            data-testid="balm-call-next-available"
            className="text-left p-4 rounded-xl border border-slate-200 hover:border-[#4A6FA5] hover:bg-[#F0F4F8]/40 transition-colors"
          >
            <div className="font-semibold text-slate-900 text-sm">Call the next available chaplain</div>
            <p className="mt-1 text-xs text-slate-600">Connect right now with whoever is available.</p>
          </button>
          <button
            type="button"
            onClick={() => setStep("callback")}
            data-testid="balm-call-request-callback"
            className="text-left p-4 rounded-xl border border-slate-200 hover:border-[#4A6FA5] hover:bg-[#F0F4F8]/40 transition-colors"
          >
            <div className="font-semibold text-slate-900 text-sm">Request a callback</div>
            <p className="mt-1 text-xs text-slate-600">A chaplain will follow up at a time that works for you.</p>
          </button>
        </div>
      )}

      {step === "nextAvailable" && (
        <div className="mt-6 space-y-4">
          <div className="p-4 rounded-xl bg-[#F0F4F8] border border-[#4A6FA5]/15 text-sm text-[#3B597B]">
            <p className="font-semibold">Preparing your demonstration…</p>
            <p className="mt-1">In the live service, you would now be connected with the next available chaplain by phone.</p>
          </div>
          <button
            type="button"
            onClick={() => setStep("done")}
            data-testid="balm-call-continue-1"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"
          >
            Continue Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </div>
      )}

      {step === "callback" && (
        <div className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">First name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sample first name"
                data-testid="balm-call-name"
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Preferred contact number</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                data-testid="balm-call-phone"
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm"
              />
            </label>
          </div>
          <Confidentiality testId="balm-call-confidentiality" />
          <button
            type="button"
            onClick={() => { setName(""); setPhone(""); setStep("done"); }}
            data-testid="balm-call-continue-2"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"
          >
            Continue Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </div>
      )}
    </div>
  );
}

function TextFlow({ onDone }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [step, setStep] = useState("form");

  if (step === "done") {
    return (
      <DemoComplete
        heading="DEMO REQUEST RECEIVED"
        body="In the live CALLACHAP service, your text request would be delivered to a chaplain, who would respond privately to your preferred contact number."
        onReset={onDone}
      />
    );
  }

  return (
    <div className="rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm">
      <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">
        Text a chaplain
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        Send a short text request. This is a demonstration — nothing is transmitted or stored.
      </p>
      <div className="mt-6 space-y-4">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">First name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sample first name"
            data-testid="balm-text-name"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Preferred contact number</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            data-testid="balm-text-phone"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Brief message</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="A short note about what would be helpful right now."
            data-testid="balm-text-message"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm resize-none"
          />
        </label>
        <Confidentiality testId="balm-text-confidentiality" />
        <button
          type="button"
          onClick={() => { setName(""); setPhone(""); setMessage(""); setStep("done"); }}
          data-testid="balm-text-continue"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"
        >
          Continue Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}

function VideoFlow({ onDone }) {
  const [date, setDate] = useState("");
  const [tod, setTod] = useState("");
  const [contact, setContact] = useState("");
  const [step, setStep] = useState("form");

  if (step === "done") {
    return (
      <DemoComplete
        heading="DEMO CONFIRMED"
        body="In the live service, a chaplain from the CALLACHAP team would confirm the video conversation at your preferred date and time-of-day."
        onReset={onDone}
      />
    );
  }

  return (
    <div className="rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm">
      <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">
        Request Remote Support
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        Pick your preferences for a face-to-face or remote conversation. This is a demonstration — no appointment is scheduled.
      </p>
      <div className="mt-6 space-y-4">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Preferred date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="balm-video-date"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm"
          />
        </label>
        <fieldset>
          <legend className="text-xs font-semibold uppercase tracking-wider text-slate-600">Time of day</legend>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {["Morning", "Afternoon", "Evening"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTod(t)}
                data-testid={`balm-video-tod-${t.toLowerCase()}`}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                  tod === t
                    ? "border-[#4A6FA5] bg-[#4A6FA5] text-white"
                    : "border-slate-200 text-slate-700 hover:border-[#4A6FA5]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="text-xs font-semibold uppercase tracking-wider text-slate-600">Preferred contact method</legend>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {["Phone", "Text", "Email"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setContact(m)}
                data-testid={`balm-video-contact-${m.toLowerCase()}`}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                  contact === m
                    ? "border-[#4A6FA5] bg-[#4A6FA5] text-white"
                    : "border-slate-200 text-slate-700 hover:border-[#4A6FA5]"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </fieldset>
        <Confidentiality testId="balm-video-confidentiality" />
        <button
          type="button"
          onClick={() => { setDate(""); setTod(""); setContact(""); setStep("done"); }}
          data-testid="balm-video-continue"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"
        >
          Continue Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}

function MessageFlow({ onDone }) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [msg, setMsg] = useState("");
  const [step, setStep] = useState("form");

  if (step === "done") {
    return (
      <DemoComplete
        heading="DEMO CONFIRMED"
        body="In the live service, a chaplain would receive your confidential message and follow up privately using your preferred contact method."
        onReset={onDone}
      />
    );
  }

  return (
    <div className="rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          <div>
            <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">
              Send a confidential message
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Share what would be helpful. Demonstration only — nothing is transmitted or stored.
            </p>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">First name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sample first name"
              data-testid="balm-message-name"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm"
            />
          </label>
          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-wider text-slate-600">Preferred contact method</legend>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {["Phone", "Text", "Email"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setContact(m)}
                  data-testid={`balm-message-contact-${m.toLowerCase()}`}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                    contact === m
                      ? "border-[#4A6FA5] bg-[#4A6FA5] text-white"
                      : "border-slate-200 text-slate-700 hover:border-[#4A6FA5]"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Brief support request</span>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={5}
              placeholder="A short note about what would be helpful right now."
              data-testid="balm-message-body"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4A6FA5] focus:ring-2 focus:ring-[#4A6FA5]/20 outline-none text-sm resize-none"
            />
          </label>
          <button
            type="button"
            onClick={() => { setName(""); setContact(""); setMsg(""); setStep("done"); }}
            data-testid="balm-message-continue"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"
          >
            Continue Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </div>
        <aside className="md:col-span-1">
          <div className="p-4 rounded-xl bg-[#F0F4F8] border border-[#4A6FA5]/15 sticky top-6">
            <div className="flex items-center gap-2 text-[#3B597B]">
              <ShieldCheck className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
              <span className="text-xs uppercase tracking-wider font-semibold">Confidentiality</span>
            </div>
            <p className="mt-2 text-sm text-[#3B597B] leading-relaxed">
              Your employer will not receive your name, message, or conversation details.
              Chaplain conversations are handled with care and discretion, subject to applicable
              legal, ethical, and safety requirements.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ----------------------------- PAGE ----------------------------- */

export default function BalmEmployeeDemo() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = "CALLACHAP Employee Demo — BALM (Demonstration)";
  }, []);

  const reset = () => setActive(null);

  return (
    <main data-testid="balm-employee-demo" className="bg-white text-slate-900">
      {/* Banner */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-amber-900">
            <Sparkles className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
            <span>
              <strong>Demonstration</strong> — BALM is a sample organization. No requests are transmitted or stored.
            </span>
          </div>
          <Link
            to="/callachap"
            data-testid="balm-back-to-landing"
            className="hidden sm:inline-flex items-center gap-1 text-xs text-amber-900 hover:text-amber-950 font-medium"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.2} /> Back to CALLACHAP
          </Link>
        </div>
      </div>

      {/* Header */}
      <section className="pt-8 md:pt-12 pb-6 md:pb-8 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Organization identity */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold" data-testid="balm-org-eyebrow">
                  BALM · Sample Organization
                </p>
                <DemoBadge />
              </div>
              <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold" data-testid="balm-provider-label">
                CALLACHAP · Provided by CCCHAPS
              </p>
              <h1 className="mt-1 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900" data-testid="balm-welcome-heading">
                Welcome to CALLACHAP
              </h1>
              <p className="mt-3 text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed" data-testid="balm-org-line">
                Confidential chaplain support provided for <span className="font-semibold text-slate-900">BALM employees</span>.
              </p>
              <p className="mt-3 text-sm md:text-base text-slate-600 max-w-2xl leading-relaxed" data-testid="balm-intent-line">
                Choose the way you would like to connect with the next available chaplain from your CALLACHAP care team.
              </p>
            </div>
            <div className="shrink-0">
              <img src="/callachap-logo.png" alt="CALLACHAP" className="h-9 md:h-11 w-auto" />
            </div>
          </div>

          {/* Privacy reassurance */}
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 max-w-3xl" data-testid="balm-privacy-block">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-[#4A6FA5] shrink-0" strokeWidth={2.2} aria-hidden="true" />
              <p className="text-sm text-slate-800 font-medium">
                Your conversation is private. Your employer does not receive the private details of your conversation.
              </p>
            </div>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed pl-6">
              Chaplain conversations are confidential within appropriate professional, ethical, safety, and legal limits. Your employer sees aggregate program utilization — not the private details of your individual conversation.
            </p>
          </div>

          {/* 24/7 access */}
          <div className="mt-3 max-w-3xl text-xs text-slate-500 leading-relaxed" data-testid="balm-247-note">
            <span className="font-semibold text-slate-700">Support when life doesn't follow business hours.</span> CALLACHAP provides 24/7 on-call chaplain access through participating organization care arrangements. Response times may vary by channel.
          </div>
        </div>
      </section>

      {/* Method chooser */}
      <section className="pt-8 md:pt-10 pb-10 md:pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {!active && (
            <div
              data-testid="balm-method-grid"
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5"
            >
              {METHODS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setActive(m.key)}
                  data-testid={`balm-method-${m.key}`}
                  className="group flex flex-col items-start gap-3 p-5 md:p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#4A6FA5] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-left h-full"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#F0F4F8] text-[#4A6FA5] group-hover:bg-[#4A6FA5] group-hover:text-white transition-colors">
                    <m.icon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} aria-hidden="true" />
                  </span>
                  <span className="font-heading text-lg md:text-xl text-slate-900 tracking-tight leading-snug">{m.title}</span>
                  <span className="text-xs md:text-sm text-slate-600 leading-relaxed">{m.label}</span>
                </button>
              ))}
            </div>
          )}

          {active && (
            <div className="space-y-6">
              <button
                type="button"
                onClick={reset}
                data-testid="balm-back-to-methods"
                className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={2.2} /> Choose a different method
              </button>
              {active === "call" && <CallFlow onDone={reset} />}
              {active === "text" && <TextFlow onDone={reset} />}
              {active === "video" && <VideoFlow onDone={reset} />}
              {active === "message" && <MessageFlow onDone={reset} />}
            </div>
          )}
        </div>
      </section>

      {/* Demo-safety + emergency notice */}
      <section className="pb-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-testid="balm-safety-notice">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200" data-testid="balm-demo-safety-note">
            <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5 text-[#4A6FA5]" strokeWidth={2} aria-hidden="true" />
            <div className="text-sm text-slate-700 leading-relaxed">
              <p className="font-semibold text-slate-900">Demonstration Environment</p>
              <p className="mt-1">
                This page demonstrates what an organization-specific CALLACHAP employee experience can look like. No real employee information, support request, or confidential message is transmitted to CCCHAPS or an employer from this demonstration.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FFF8ED] border border-[#F5D68A]/60">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-[#B8791A]" strokeWidth={2} aria-hidden="true" />
            <p className="text-sm text-[#7A4F0F] leading-relaxed">
              CALLACHAP is not an emergency, medical, or psychiatric service. If you are experiencing a life-threatening emergency, call 911.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
